/**
 * [Gurumi 툴바 프리셋]
 * - 역할: 페이지/필드별 에디터 기능 on·off 해석
 * - 작성/수정일: 2026-08-12
 */

/** @typedef {Record<string, boolean>} GurumiFeatureFlags */

/**
 * write 기본 — 게시판 본문 전체 기능
 * @type {GurumiFeatureFlags}
 */
export const GURUMI_FEATURES_WRITE = {
  blockMenu: true,
  bold: true,
  italic: true,
  color: true,
  link: true,
  image: true,
  youtube: true,
  align: true,
  linkPreview: true,
  fontVariant: true,
  heading: true,
  blockquote: true,
  codeBlock: true,
  horizontalRule: true,
  orderedList: true,
  bulletList: true,
};

/**
 * comment — 댓글 간소화
 * @type {GurumiFeatureFlags}
 */
export const GURUMI_FEATURES_COMMENT = {
  blockMenu: false,
  bold: true,
  italic: true,
  color: false,
  link: true,
  image: true,
  youtube: true,
  align: false,
  linkPreview: false,
  fontVariant: false,
  heading: false,
  blockquote: false,
  codeBlock: false,
  horizontalRule: false,
  orderedList: false,
  bulletList: false,
};

/**
 * profile — 아티스트/갤러리 소개 (타이포·미디어, 색·형광펜 제외)
 * @type {GurumiFeatureFlags}
 */
export const GURUMI_FEATURES_PROFILE = {
  ...GURUMI_FEATURES_WRITE,
  color: false,
};

/**
 * simple — 짧은 안내/메모
 * @type {GurumiFeatureFlags}
 */
export const GURUMI_FEATURES_SIMPLE = {
  blockMenu: false,
  bold: true,
  italic: true,
  color: false,
  link: true,
  image: false,
  youtube: false,
  align: false,
  linkPreview: false,
  fontVariant: false,
  heading: false,
  blockquote: false,
  codeBlock: false,
  horizontalRule: false,
  orderedList: false,
  bulletList: false,
};

const PRESET_MAP = {
  write: GURUMI_FEATURES_WRITE,
  comment: GURUMI_FEATURES_COMMENT,
  profile: GURUMI_FEATURES_PROFILE,
  simple: GURUMI_FEATURES_SIMPLE,
};

/**
 * [Gurumi 툴바 프리셋]
 * - 역할: mode/preset/features 병합 → 최종 플래그
 * - 작성/수정일: 2026-08-12
 *
 * @param {{ mode?: string, preset?: string, features?: Partial<GurumiFeatureFlags> }} options
 * @returns {{ preset: string, features: GurumiFeatureFlags }}
 */
export function resolveGurumiFeatures(options = {}) {
  const mode = options.mode || 'write';
  let preset = (options.preset || '').trim() || (mode === 'comment' ? 'comment' : 'write');
  if (!PRESET_MAP[preset]) {
    preset = mode === 'comment' ? 'comment' : 'write';
  }

  const base = { ...PRESET_MAP[preset] };
  const override = options.features && typeof options.features === 'object' ? options.features : {};
  const features = { ...base };

  Object.keys(override).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(features, key)) {
      features[key] = !!override[key];
    }
  });

  // comment 모드는 프리셋과 무관하게 댓글 제약 유지
  if (mode === 'comment') {
    Object.assign(features, GURUMI_FEATURES_COMMENT);
    preset = 'comment';
  }

  return { preset, features };
}

/**
 * [Gurumi 툴바 프리셋]
 * - 역할: textarea data-gurumi-* 속성 파싱
 * - 작성/수정일: 2026-08-12
 *
 * @param {HTMLElement|null} el
 * @returns {{ preset: string, features: Partial<GurumiFeatureFlags> }}
 */
export function readGurumiFeaturesFromElement(el) {
  if (!el || !el.getAttribute) {
    return { preset: '', features: {} };
  }

  const preset = (el.getAttribute('data-gurumi-preset') || '').trim();
  let features = {};
  const raw = el.getAttribute('data-gurumi-features');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        features = parsed;
      }
    } catch (_) {
      // 잘못된 JSON은 무시
    }
  }

  return { preset, features };
}
